import { useState } from 'react'
import {
  Award,
  BookOpen,
  Download,
  Eye,
  GraduationCap,
  Lock,
  Mail,
  Pencil,
  Plus,
  ShieldAlert,
  Trash2,
  UserCog,
  Users,
} from 'lucide-react'
import Avatar from '../components/Avatar'
import Button from '../components/Button'
import Card from '../components/Card'
import ConfirmDialog from '../components/ConfirmDialog'
import DataTable from '../components/DataTable'
import IconButton from '../components/IconButton'
import Modal from '../components/Modal'
import PageHeader from '../components/PageHeader'
import ResultDialog from '../components/ResultDialog'
import Skeleton from '../components/Skeleton'
import StatStrip from '../components/StatStrip'
import StatusBadge from '../components/StatusBadge'
import FormRow from '../components/FormRow'
import TextField from '../components/TextField'
import { EmptyState, ErrorState, LoadingState } from '../components/States'
import { localize } from '../lib/i18n'
import { K } from './KitPage.strings'
import './KitPage.css'

/**
 * Live preview of the shared kit. Development scaffolding, not a legacy screen —
 * it exists so the foundation can be reviewed before pages are built on it, and it
 * is not linked from the side menu.
 *
 * Rows are real `Profiles` records from RawamDemo.sql, picked to cover every status
 * badge combination the legacy `getStatusTags` renderer can produce.
 */
const previewUsers = localize([
  {id: "4351b2be-0a49-44cc-b965-02b1409807df", userName: "BilqisBakr", name: "بلقيس بكر", email: "user1@example.invalid", groups: ["المدراء", "الرؤية الموثوقة", "في", "test11", "test222", "افتراضي"], status: [{color: "danger", status: "معطل"}, {color: "dark", status: "منتهي الصلاحية"}]},
  {id: "1958a7d7-615d-43eb-84cc-0f9c7f3da431", userName: "bilqis", name: "بلقيس بكر", email: "user2@example.invalid", groups: ["المدراء", "غيدا", "الرؤية الموثوقة", "test11", "test222", "افتراضي"], status: [{color: "danger", status: "معطل"}]},
  {id: "0e7c160d-5a98-415e-8e3e-1ec0a017aaab", userName: "icuser3", name: "نافع المطير", email: "user3@example.invalid", groups: ["المدراء", "افتراضي"], status: [{color: "dark", status: "منتهي الصلاحية"}]},
  {id: "35276cda-5b1e-4646-bef5-cd483e3aacfe", userName: "test-account", name: "Wajdy Essam Ahmed", email: "user4@example.invalid", groups: ["المدراء", "افتراضي"], status: [{color: "info", status: "مستقيل"}]},
  {id: "4786b8e2-34eb-4c41-b129-d2cdd712872e", userName: "coeiaadmin", name: "coeiaadmin", email: "user5@example.invalid", groups: ["المدراء", "افتراضي"], status: [{color: "warning", status: "بإنتظار التفعيل"}]},
  {id: "a228cb74-3b51-46d6-815c-daaa5fbe24b5", userName: "siddigmh", name: "الصديق محمد الزبير", email: "user6@example.invalid", groups: ["المدراء", "تجربة الاصطياد", "تجربة إصطياد 5", "تجربة قالب امانة الرياض", "صديق - إصطياد", "افتراضي"], status: [{color: "success", status: "مفعل"}]},
  {id: "4077e2c3-989f-49d7-ac04-485b8cbde772", userName: "rawamadmin", name: "Wajdy Essam Ahmed", email: "user7@example.invalid", groups: ["المدراء", "تجربة الاصطياد", "غيدا", "PRIVATE-Phishing", "test11", "test222", "تجربة قالب امانة الرياض", "ليب", "افتراضي"], status: [{color: "success", status: "مفعل"}]},
  {id: "12c6b12f-56d3-4632-ae94-5088716885f4", userName: "Amal", name: "امل محمد الماحي", email: "user8@example.invalid", groups: ["القسم النسائي", "test", "تجربة الاصطياد", "غيدا", "تجربة الاصطياد ٢", "TestFAy", "تنمية", "ليب"], status: [{color: "success", status: "مفعل"}]},
  {id: "69faaf16-337e-4664-95f6-55faa57bf709", userName: "shahad", name: "شهد آل سليمان", email: "user9@example.invalid", groups: ["الطلاب"], status: [{color: "success", status: "مفعل"}]},
  {id: "438c1e46-9150-4237-ae46-7be940f8533e", userName: "Raghad", name: "رغد ال سليمان", email: "user10@example.invalid", groups: ["الطلاب"], status: [{color: "success", status: "مفعل"}]},
  {id: "125aabb1-4397-4fdf-b60d-1485ff7db790", userName: "Dhai", name: "ضي فهد الحربي", email: "user11@example.invalid", groups: ["الطلاب"], status: [{color: "success", status: "مفعل"}]},
  {id: "4a9fc8d8-e0e7-4515-b122-0442da41c4ce", userName: "Nafel", name: "نافل بن نافل السبيعي", email: "user12@example.invalid", groups: ["المدراء", "افتراضي"], status: [{color: "danger", status: "موقوف"}]},
  {id: "0272b8b8-1eae-42a0-aa35-25864bfb03b7", userName: "meshal", name: "مشعل امجد الحربي", email: "user13@example.invalid", groups: ["الطلاب", "test"], status: [{color: "danger", status: "موقوف"}]},
  {id: "3023bac0-19e2-4501-b579-c5f03c98c16b", userName: "sami.alqarni", name: "سامي القرني", email: "user14@example.invalid", groups: ["المدراء", "افتراضي"], status: [{color: "success", status: "مفعل"}]},
  {id: "f199e088-3721-48f9-808e-2cb1f70fc239", userName: "Raghad.kharabah", name: "رغد", email: "user15@example.invalid", groups: ["المدراء", "افتراضي"], status: [{color: "success", status: "مفعل"}]},
  {id: "4339049f-42de-48ce-922e-207f7705966e", userName: "amealharbi", name: "أميرة الحربي", email: "user16@example.invalid", groups: ["Saudi exim"], status: [{color: "success", status: "مفعل"}]},
])

/** One card per tone, so the strip doubles as the tone reference. */
const previewStats = [
  { id: 'programs', icon: GraduationCap, label: K.stats.programs, value: 7 },
  { id: 'courses', icon: BookOpen, label: K.stats.courses, value: 42, tone: 'info' },
  { id: 'passed', icon: Award, label: K.stats.passed, value: 1284, tone: 'success' },
  { id: 'reports', icon: ShieldAlert, label: K.stats.reports, value: 18, tone: 'warning' },
  { id: 'empty', icon: Users, label: K.stats.remaining, value: '—', tone: 'gold' },
]

export default function KitPage() {
  const [modalOpen, setModalOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [result, setResult] = useState(null)
  const [statFilter, setStatFilter] = useState(null)

  const columns = [
    { key: 'index', header: K.table.index, width: '64px', sortable: false, searchable: false,
      render: (_row, index) => index + 1 },
    { key: 'userName', header: K.table.userName,
      render: (row) => <a href="#kit">{row.userName}</a> },
    { key: 'name', header: K.table.name,
      render: (row) => (
        <span className="kit__identity">
          <Avatar name={row.name} size="sm" />
          <bdi>{row.name}</bdi>
        </span>
      ) },
    { key: 'email', header: K.table.email,
      render: (row) => <span dir="ltr" className="kit__mail">{row.email}</span> },
    { key: 'groups', header: K.table.groups,
      value: (row) => row.groups.join(' '),
      render: (row) => (
        <span className="kit__groups">
          {row.groups.slice(0, 2).map((group) => (
            <StatusBadge key={group} tone="primary" plain>{group}</StatusBadge>
          ))}
          {row.groups.length > 2 && (
            <StatusBadge tone="dark" plain>+{row.groups.length - 2}</StatusBadge>
          )}
        </span>
      ) },
    { key: 'status', header: K.table.status, sortable: false,
      value: (row) => row.status.map((badge) => badge.status).join(' '),
      render: (row) => (
        <span className="kit__groups">
          {row.status.map((badge) => (
            <StatusBadge key={badge.status} tone={badge.color}>{badge.status}</StatusBadge>
          ))}
        </span>
      ) },
    { key: 'actions', header: <Pencil size={15} strokeWidth={1.75} />, align: 'center',
      sortable: false, searchable: false, width: '110px',
      render: () => (
        <span className="kit__row-actions">
          <IconButton icon={Pencil} label={K.table.editProfile} onClick={() => setModalOpen(true)} />
          <IconButton icon={Lock} label={K.table.editStatus} tone="primary" onClick={() => setConfirmOpen(true)} />
        </span>
      ) },
  ]

  return (
    <>
      <PageHeader
        title={K.title}
        subtitle={K.subtitle}
        actions={
          <>
            <Button variant="ghost" onClick={() => setResult({ result: false, message: K.exportFailed })}>
              <Download size={16} strokeWidth={1.75} /> {K.export}
            </Button>
            <Button onClick={() => setModalOpen(true)}>
              <Plus size={16} strokeWidth={1.75} /> {K.add}
            </Button>
          </>
        }
      />

      {/* Cards double as filter controls here, which is also how the Groups
          screen uses them — click one and it holds its tone. */}
      <StatStrip
        label={K.stats.label}
        items={previewStats}
        activeId={statFilter}
        onSelect={(id) => setStatFilter((current) => (current === id ? null : id))}
      />

      <DataTable
        title={K.table.title}
        icon={Users}
        columns={columns}
        rows={previewUsers}
        toolbar={
          <>
            <IconButton icon={Mail} label={K.table.sendMail} tone="success" size="md" filled />
            <IconButton icon={Eye} label={K.table.viewDetails} tone="info" size="md" filled />
            <IconButton icon={Trash2} label={K.form.delete} tone="danger" size="md" filled
              onClick={() => setConfirmOpen(true)} />
          </>
        }
      />

      <div className="kit__grid">
        <Card title={K.states.title}>
          <LoadingState />
          <EmptyState title={K.states.empty} />
          <ErrorState title={K.states.errorTitle} description={K.states.errorDesc} />

          {/* The placeholder a list shows instead of a spinner. Rendered here as
              loose bars since the kit has no table to hold open. */}
          <div className="kit__stack" style={{ marginTop: 'var(--space-6)' }}>
            <Skeleton variant="text" width="46%" />
            <Skeleton variant="text" width="72%" />
            <Skeleton variant="text" width="58%" />
          </div>
        </Card>

        <Card title={K.form.title}>
          <div className="kit__stack">
            <FormRow label={K.form.fullName}>{(id) => <TextField id={id} placeholder={K.form.fullName} />}</FormRow>
            {/* A hint longer than a line folds into an "i" in the label; a short
                one stays under the control. FormRow decides, not the caller. */}
            <FormRow
              label={K.form.email}
              hint={K.form.emailHint}
            >
              {(id) => <TextField id={id} placeholder="user17@example.invalid" />}
            </FormRow>
            <div className="kit__row">
              <Button onClick={() => setResult({ result: true, message: K.form.saved })}>
                {K.form.save}
              </Button>
              <Button variant="ghost" onClick={() => setConfirmOpen(true)}>{K.form.delete}</Button>
            </div>
            <div className="kit__row">
              <StatusBadge tone="success">{K.badges.active}</StatusBadge>
              <StatusBadge tone="danger">{K.badges.suspended}</StatusBadge>
              <StatusBadge tone="warning">{K.badges.waiting}</StatusBadge>
              <StatusBadge tone="info">{K.badges.resigned}</StatusBadge>
              <StatusBadge tone="dark">{K.badges.expired}</StatusBadge>
              <StatusBadge tone="gold" plain>{K.badges.featured}</StatusBadge>
              <StatusBadge tone="danger" solid>{K.badges.blocked}</StatusBadge>
            </div>
          </div>
        </Card>
      </div>

      {/* The action matrix: one row per variant, one column per size, so a
          regression in any cell is visible without hunting for a screen that
          happens to use it. */}
      <Card title={K.buttons.title}>
        <div className="kit__stack">
          <div className="kit__row">
            <Button>{K.buttons.primary}</Button>
            <Button variant="ghost">{K.buttons.secondary}</Button>
            <Button variant="soft">{K.buttons.soft}</Button>
            <Button variant="quiet">{K.buttons.quiet}</Button>
            <Button variant="danger">{K.buttons.danger}</Button>
            <Button variant="success">{K.buttons.success}</Button>
            <Button variant="warning">{K.buttons.warning}</Button>
          </div>
          <div className="kit__row">
            <Button size="sm">{K.buttons.small}</Button>
            <Button size="md">{K.buttons.medium}</Button>
            <Button size="lg">{K.buttons.large}</Button>
            <Button disabled>{K.buttons.disabled}</Button>
            <Button variant="ghost" disabled>{K.buttons.disabledGhost}</Button>
          </div>
          <div className="kit__row">
            <Button icon aria-label={K.add}>
              <Plus size={16} strokeWidth={2} />
            </Button>
            <Button variant="ghost" icon aria-label={K.buttons.edit}>
              <Pencil size={16} strokeWidth={1.75} />
            </Button>
            <IconButton icon={Pencil} label={K.buttons.edit} />
            <IconButton icon={Trash2} label={K.form.delete} tone="danger" />
            <IconButton icon={Eye} label={K.buttons.view} tone="info" filled size="md" />
          </div>
        </div>
      </Card>

      <Modal
        open={modalOpen}
        title={K.modal.title}
        subtitle={previewUsers[5].name}
        icon={UserCog}
        hint={K.modal.hint}
        size="lg"
        onClose={() => setModalOpen(false)}
        footer={<Button onClick={() => setModalOpen(false)}>{K.form.save}</Button>}
      >
        <div className="kit__stack">
          <FormRow label={K.form.fullName}>{(id) => <TextField id={id} defaultValue={previewUsers[5].name} />}</FormRow>
          <FormRow label={K.form.email}>{(id) => <TextField id={id} defaultValue={previewUsers[5].email} />}</FormRow>
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmOpen}
        message={K.modal.confirm}
        onConfirm={() => {
          setConfirmOpen(false)
          setResult({ result: true, message: K.modal.deleted })
        }}
        onCancel={() => setConfirmOpen(false)}
      />

      <ResultDialog result={result} closeLabel={K.modal.close} onClose={() => setResult(null)} />
    </>
  )
}
